import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

export const LANG_STORAGE_KEY = "sbdr_lang";
export const SUPPORTED_LANGS = ["es", "en", "pt"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

// Locales are loaded on demand — only the active language is fetched at boot,
// which keeps ~50 KB gzip of JSON out of the initial landing bundle.
const loaders: Record<SupportedLang, () => Promise<{ default: Record<string, unknown> }>> = {
  es: () => import("./locales/es.json"),
  en: () => import("./locales/en.json"),
  pt: () => import("./locales/pt.json"),
};

const loaded = new Set<SupportedLang>();

async function ensureLoaded(lang: SupportedLang) {
  if (loaded.has(lang)) return;
  const mod = await loaders[lang]();
  i18n.addResourceBundle(lang, "translation", mod.default, true, true);
  loaded.add(lang);
}

function detectInitial(): SupportedLang {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored && (SUPPORTED_LANGS as readonly string[]).includes(stored)) {
      return stored as SupportedLang;
    }
    const nav = (navigator.language || "es").slice(0, 2).toLowerCase();
    if ((SUPPORTED_LANGS as readonly string[]).includes(nav)) return nav as SupportedLang;
  } catch {}
  return "es";
}

const initial = detectInitial();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {}, // populated lazily
    lng: initial,
    fallbackLng: "es",
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ["localStorage"],
    },
    partialBundledLanguages: true,
    // Locales arrive after mount; re-render components when a bundle is added.
    react: { bindI18nStore: "added" },
  });

// Kick off the initial language load — do NOT block module init.
void ensureLoaded(initial);
// Preload the fallback in the background so a missing key never flashes.
if (initial !== "es") void ensureLoaded("es");

export async function setLang(lang: SupportedLang) {
  await ensureLoaded(lang);
  await i18n.changeLanguage(lang);
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {}
}

export default i18n;
